import React, { useState, useRef } from 'react';
import { AttachedDocument } from '../types';
import { formatFileSize, getFileIcon } from '../firebase/documents.service';
import { X, Upload } from '../../../icons';

interface DocumentUploadProps {
  documents: AttachedDocument[];
  onUpload: (file: File) => Promise<void>;
  onDelete: (documentId: string) => void;
  disabled?: boolean;
  maxDocuments?: number;
}

/**
 * DocumentUpload - Component for uploading and managing documents
 * 
 * Features:
 * - Drag and drop support
 * - File type validation
 * - Size display
 * - Delete functionality
 */
const DocumentUpload: React.FC<DocumentUploadProps> = ({
  documents,
  onUpload,
  onDelete,
  disabled = false,
  maxDocuments = 10,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canAddMore = documents.length < maxDocuments;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && canAddMore) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled || !canAddMore) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadFile(files[0]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setError(null);

    try {
      await onUpload(file);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload mislukt');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (docId: string) => {
    if (disabled) return;
    onDelete(docId);
  };

  return (
    <div className="space-y-3">
      {/* Document List */}
      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-lg flex-shrink-0">{getFileIcon(doc.type)}</span>
                <div className="min-w-0 flex-1">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-900 hover:text-orange-600 truncate block"
                    title={doc.name}
                  >
                    {doc.name}
                  </a>
                  <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                </div>
              </div>
              {!disabled && (
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  title="Verwijderen"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {canAddMore && !disabled && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-4 text-center transition-colors
            ${isDragging 
              ? 'border-orange-400 bg-orange-50' 
              : 'border-gray-300 hover:border-gray-400'
            }
            ${isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
          `}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
            disabled={disabled || isUploading}
          />
          
          <div className="flex flex-col items-center gap-2">
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
                <p className="text-sm text-gray-600">Uploaden...</p>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 text-gray-400" />
                <p className="text-sm text-gray-600">
                  <span className="text-orange-600 font-medium">Klik</span> of sleep een bestand
                </p>
                <p className="text-xs text-gray-400">
                  PDF, Word, Excel, afbeeldingen (max 10MB)
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Max reached */}
      {!canAddMore && !disabled && (
        <p className="text-xs text-gray-500 text-center">
          Maximum aantal documenten ({maxDocuments}) bereikt
        </p>
      )}
    </div>
  );
};

export default DocumentUpload;
